<template>
  <main class="content-page">
    <b-container class="py-5">
      <header class="page-hero text-center mx-auto mb-5">
        <p class="eyebrow">CASH BACK 101</p>
        <h1>How Cash Back Works at CashBoomerang</h1>
        <p class="lead text-muted">A transparent guide to tracked shopping trips, pending rewards, retailer approval, and payouts.</p>
        <p class="reviewed">Reviewed July 26, 2026</p>
      </header>

      <section class="steps-grid mb-5" aria-labelledby="three-steps">
        <h2 id="three-steps" class="text-center mb-4">Three steps from click to cash back</h2>
        <b-row>
          <b-col md="4" class="mb-4" v-for="step in steps" :key="step.number">
            <article class="step-card h-100">
              <span class="step-number">{{ step.number }}</span>
              <h3>{{ step.title }}</h3>
              <p>{{ step.text }}</p>
            </article>
          </b-col>
        </b-row>
      </section>

      <section class="answer-block mb-5">
        <h2>Which purchases are eligible?</h2>
        <p>Only offers explicitly marked <strong>Cash Back Eligible</strong> can earn a reward. Eligibility, rates, exclusions, and confirmation times are set by each participating retailer or affiliate network. Opening a general shopping link does not automatically create cash back.</p>
        <div class="notice">
          <strong>Amazon notice:</strong> Amazon product links on CashBoomerang are disclosed affiliate shopping links, not CashBoomerang cash-back offers. Amazon Associates rules do not allow us to reward users for using those links.
        </div>
      </section>

      <section class="mb-5">
        <h2>What do reward statuses mean?</h2>
        <div class="status-table" role="table" aria-label="Cash back status definitions">
          <div class="status-row" v-for="status in statuses" :key="status.name" role="row">
            <strong role="cell">{{ status.name }}</strong>
            <span role="cell">{{ status.description }}</span>
          </div>
        </div>
      </section>

      <section class="answer-block">
        <h2>Before you shop</h2>
        <ul>
          <li>Sign in before activating an eligible offer.</li>
          <li>Start with an empty cart and complete the order in the same browser session.</li>
          <li>Disable ad blockers for the shopping trip and avoid unapproved coupon codes.</li>
          <li>Keep the retailer confirmation email until the reward is approved.</li>
        </ul>
        <router-link to="/cashback-guide" class="btn btn-success mt-3">Read the complete cash-back guide</router-link>
      </section>
    </b-container>
  </main>
</template>

<script>
export default {
  name: 'HowCashbackWorks',
  data() {
    return {
      steps: [
        { number: '1', title: 'Choose an eligible offer', text: 'Look for an offer that clearly displays a cash-back rate and its terms. Standard affiliate shopping links are labeled separately.' },
        { number: '2', title: 'Activate and shop', text: 'Sign in and use the offer button so the participating network can associate the shopping trip with your CashBoomerang account.' },
        { number: '3', title: 'Wait for confirmation', text: 'Rewards begin as pending. They become available only after the retailer confirms the order and the return or cancellation period has passed.' }
      ],
      statuses: [
        { name: 'Clicked', description: 'A shopping trip was started; no purchase has been reported yet.' },
        { name: 'Pending', description: 'A purchase was reported but is still being reviewed by the retailer.' },
        { name: 'Available', description: 'The retailer approved the transaction and the reward can count toward payout.' },
        { name: 'Reversed', description: 'The order was returned, cancelled, ineligible, duplicated, or rejected by the retailer.' },
        { name: 'Paid', description: 'The available reward was included in a completed payout.' }
      ]
    }
  }
}
</script>

<style scoped>
.content-page { background: #f7faf7; color: #203126; min-height: 75vh; }
.page-hero { max-width: 780px; }
.page-hero h1 { font-size: clamp(2.1rem, 5vw, 3.4rem); font-weight: 700; }
.eyebrow { color: #198754; font-weight: 700; letter-spacing: .14em; }
.reviewed { font-size: .875rem; color: #68776d; }
.step-card, .answer-block { background: #fff; border: 1px solid #deebe1; border-radius: 18px; padding: 28px; }
.step-card h3 { font-size: 1.25rem; margin-top: 1rem; }
.step-number { display: inline-grid; place-items: center; width: 42px; height: 42px; border-radius: 50%; background: #198754; color: #fff; font-weight: 700; }
.notice { border-left: 4px solid #d59d16; background: #fff8e6; padding: 16px 18px; margin-top: 1.25rem; }
.status-table { border: 1px solid #deebe1; border-radius: 14px; overflow: hidden; }
.status-row { display: grid; grid-template-columns: 140px 1fr; gap: 20px; padding: 16px 20px; background: #fff; border-bottom: 1px solid #deebe1; }
.status-row:last-child { border-bottom: 0; }
@media (max-width: 576px) { .status-row { grid-template-columns: 1fr; gap: 4px; } }
</style>

